<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { ComparedSvgFooterData, FooterData } from '@/types/footer';
import MinusFillIcon from '@/assets/icons/MinusIcon.vue';
import PlusFillIcon from '@/assets/icons/PlusIcon.vue';
import { Amenity } from '@/types';
import { SalesLandFooterOrder } from '@/constant';
import SoldPendingStatus from '@/assets/icons/SoldPendingStatus.vue';
import SoldContingencyStatus from '@/assets/icons/SoldContengencyStatus.vue';
import AmenityIcon from '@/assets/icons/AmenityIcon.vue';

const props = defineProps<{
  data: FooterData[];
  selectedNeighborhoodIds: string[];
  amenities: Amenity[];
  matchedSvgfooterData: ComparedSvgFooterData[];
}>();

const { selectedNeighborhoodIds, data, matchedSvgfooterData, amenities } =
  toRefs(props);
const emit = defineEmits(['setLegends']);
const isOpen = ref(true);

const matchLegendItemWithSvg = (footerData: FooterData[]): FooterData[] => {
  return footerData
    .map((item) => {
      const matchedData = matchedSvgfooterData.value.filter((d) =>
        d.status.includes(item.status.toLowerCase())
      );

      const neighborhoods = matchedData.map((d) => d.neighborhood_code);
      return { ...item, neighborhoods };
    })
    .filter((item) => item.neighborhoods.length > 0);
};

const sortItems = (updateFooterData: FooterData[]): FooterData[] => {
  const sorted: FooterData[] = [];
  for (const order of SalesLandFooterOrder) {
    const found = updateFooterData.find((el) => el.status === order);
    if (found) sorted.push(found);
  }
  return sorted;
};

const legendItems = computed(() => {
  let filtered = matchLegendItemWithSvg(data.value);

  if (selectedNeighborhoodIds.value.length) {
    filtered = filtered.filter((d) =>
      d.neighborhoods.some((n) => selectedNeighborhoodIds.value.includes(n))
    );
  }

  filtered = sortItems(filtered);

  const legendArr = filtered.map((plan) => plan.status).toString();
  emit('setLegends', legendArr);

  return filtered;
});

const toggleLegend = () => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div class="legend-container">
    <div class="legend-header" @click="toggleLegend">
      <h4>Legend</h4>
      <span>
        <MinusFillIcon v-if="isOpen" />
        <PlusFillIcon v-else />
      </span>
    </div>
    <ul v-show="isOpen" class="legend-list">
      <li v-for="(item, index) in legendItems" :key="index">
        <SoldPendingStatus
          v-if="item.status === 'Sold Pending'"
          svgClass="color-box"
        />
        <SoldContingencyStatus
          v-else-if="item.status === 'Sold Contingency'"
          svgClass="color-box"
        />

        <span
          v-else
          class="color-box"
          :style="{
            backgroundColor: item.color,
          }"
        ></span>
        {{ item.status === 'QMI' ? 'Quick Move-In' : item.status }}
      </li>

      <li class="embfooter__status__list" v-if="amenities?.length">
        <AmenityIcon />
        Amenity
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.legend-container {
  position: absolute;
  top: 148px;
  right: 20px;
  background-color: #fff;
  border-radius: 4px;
  width: 165px;
  z-index: 100;
  font-size: 13px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.15);
}

.legend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 14.5px;
  cursor: pointer;
  user-select: none;
  h4 {
    color: $black;
    font-weight: 600;
    font-size: 14px;
  }
}

.legend-list {
  list-style: none;
  padding: 0 14.5px 15px;
  margin: 0;

  li {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: $gray-600;
  }
  li:not(:last-child) {
    margin-bottom: 8px;
  }

  .color-box {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    border-radius: 3px;
  }
}

.ammenity-icon {
  width: 11px;
  height: 17px;
}
</style>
